<template>
  <SDialog v-model:open="modalVisible">
    <SDialogContent
      class="download-tickets-popup !w-[80%] !max-w-[80%] !max-h-[85vh] !grid-rows-[auto_1fr]"
      :show-close-button="false"
      @open-auto-focus.prevent
    >
      <SDialogHeader>
        <div class="flex items-center justify-between gap-4">
          <SDialogTitle>{{ $t("completeorder.list-of-tickets") }}</SDialogTitle>
          <div class="flex items-center gap-3">
            <SButton
              v-if="hasInvoiceEnabled"
              type="button"
              variant="outline"
              @click.stop="downloadInvoice"
              :disabled="loading"
              data-cy="download-invoice-button"
            >
              <DownloadIcon />
              {{ $t("button.download-invoice") }}
            </SButton>
            <SButton
              type="button"
              @click.stop="downloadAllTickets"
              :disabled="loading"
              data-cy="download-all-tickets-button"
            >
              <DownloadIcon />
              {{ $t("button.download-all") }}
            </SButton>
            <button
              type="button"
              class="ml-2 inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus:outline-none"
              :aria-label="$t('button.close')"
              @click="closeModal"
            >
              <XIcon class="h-5 w-5" />
            </button>
          </div>
        </div>
      </SDialogHeader>
      <!-- Spinner overlay -->
      <div v-if="loading" class="spinner-overlay">
        <LoaderCircleIcon class="s-icon-spin spinner" />
      </div>
      <div class="overflow-y-auto min-h-0">
        <el-row
          class="sticky top-0 z-10 bg-white"
          style="
            font-weight: 700;
            border-bottom: 1px solid grey;
            padding-bottom: 15px;
            padding-top: 15px;
          "
        >
          <el-col class="column" :span="4">{{
            $t("common.ticket-name")
          }}</el-col>
          <el-col class="column" :span="4">{{
            $t("common.price-name")
          }}</el-col>
          <el-col class="column" :span="4">{{
            $t("common.price-value")
          }}</el-col>
          <el-col class="column" :span="4">
            {{ $t("orders.customer-full-name") }}
          </el-col>
          <el-col class="column" :span="4">{{ $t("common.bar-code") }}</el-col>
          <el-col class="column" :span="4"></el-col>
        </el-row>
        <div v-for="(data, index) in ticketsInfo" :key="data.token.token">
          <el-row
            style="padding-bottom: 15px; padding-top: 15px"
            :style="
              index !== ticketsInfo.length - 1
                ? 'border-bottom: 1px solid grey'
                : ''
            "
          >
            <el-col
              style="text-align: left; font-weight: 700"
              :span="4"
              class="d-flex fl-col fl-j-c column"
            >
              <p>{{ data.line.categoryName }}</p>
            </el-col>
            <el-col :span="4" class="d-flex fl-col fl-j-c column"
              ><p>{{ data.line.priceName }}</p></el-col
            >
            <el-col :span="4" class="d-flex fl-col fl-j-c"
              ><p>
                {{ data.line.unitPrice.toFixed(2) + " " + currency }}
              </p></el-col
            >
            <el-col :span="4" class="d-flex fl-col fl-j-c column"
              ><p>{{ data.firstName + " " + data.lastName }}</p></el-col
            >
            <el-col :span="4" class="d-flex fl-col fl-j-c"
              ><p>
                {{ data.token.barcode ? data.token.barcode : "-" }}
              </p></el-col
            >
            <el-col
              :span="4"
              style="text-align: right; padding-right: 10px"
              class="d-flex fl-col fl-j-c"
            >
              <div class="flex gap-1 justify-end">
                <!-- download badges (membership tickets) -->
                <SButton
                  v-if="isMembershipLine(data.line)"
                  type="button"
                  variant="outline"
                  size="icon"
                  class="download-button"
                  :disabled="loading"
                  @click="downloadBadge(data.token)"
                >
                  <IdCardIcon />
                </SButton>
                <!-- download gift cards -->
                <SButton
                  v-if="data.line.itemType === OrderLinePublicType.VOUCHER"
                  type="button"
                  variant="outline"
                  size="icon"
                  class="download-button"
                  :disabled="loading"
                  @click="downloadGiftcard(data.token)"
                >
                  <IdCardIcon />
                </SButton>
                <!-- download tickets -->
                <SButton
                  type="button"
                  variant="outline"
                  size="icon"
                  class="download-button"
                  :disabled="loading"
                  @click.stop="downloadTicket(data.token)"
                >
                  <DownloadIcon />
                </SButton>
              </div>
            </el-col>
          </el-row>
        </div>
      </div>
    </SDialogContent>
  </SDialog>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useOrderStore } from "@smeetz/order-store";
import {
  OrderLine,
  OrderLinePublicType,
  OrderToken,
  TokenStatus,
} from "@smeetz/order-common";
import { dialogVisibleForStatus } from "@/utils/vue-helpers/modal";
import { ModalComponents, ModalStatus } from "@/constants/modal";
import ModalModule from "@/store/modules/modal";
import POSConfigModule from "@/store/modules/pos-config";
import NotificationService from "@/services/notification.service";
import {
  generateOrderInvoice,
  getDownloadOrderTicket,
  getDownloadOrderTickets,
  postGenerateOrderFiles,
  downloadOrderInvoice,
} from "@/api/booking/booking";
import { getBadgePdf, getGiftcardPdf } from "@/api/gql/badges";
import { base64toPdfBlob } from "@/utils/vue-helpers/badges";
import { getOrderCustomFieldAnswers } from "@/utils/vue-helpers/custom-fields.helper";
import {
  ICustomFieldAnswer,
  InformationType,
} from "@/types/db/product-category";
import { LogLevel, LogService } from "@/services/logging";
import { hasInvoiceEnabled } from "@/utils/vue-helpers/compliance";
import {
  SButton,
  SDialog,
  SDialogContent,
  SDialogHeader,
  SDialogTitle,
} from "@smeetz/ui-core";
import {
  DownloadIcon,
  IdCardIcon,
  LoaderCircleIcon,
  XIcon,
} from "@smeetz/ui-icons";

/** Line types whose tickets carry a membership badge (NOL equivalent of the
 * legacy MEMBERSHIP_TICKETS PSS types). */
const MEMBERSHIP_LINE_TYPES = [
  OrderLinePublicType.MEMBERSHIP,
  OrderLinePublicType.MEMBERSHIP_VOUCHER,
  OrderLinePublicType.MEMBERSHIP_SEATED,
];

/** One downloadable ticket row: an ACTIVE token joined to its order line. */
interface TicketRow {
  token: OrderToken;
  line: OrderLine;
  firstName: string;
  lastName: string;
}

export default defineComponent({
  name: ModalComponents.DownloadTickets,
  components: {
    SButton,
    SDialog,
    SDialogContent,
    SDialogHeader,
    SDialogTitle,
    DownloadIcon,
    IdCardIcon,
    LoaderCircleIcon,
    XIcon,
  },
  setup() {
    const { t } = useI18n({});
    const orderStore = useOrderStore();
    const modalVisible = dialogVisibleForStatus(ModalStatus.DownloadTickets);
    const loading = ref(false);

    const currency = computed(
      () => orderStore.selectedOrder?.orderSession?.currency ?? ""
    );

    // Ticket custom-field answers (resolved with each answer's question, so they
    // carry `informationType`). Loaded once on open; used to override per-ticket
    // holder names below.
    const ticketAnswers = ref<ICustomFieldAnswer[]>([]);
    onMounted(async () => {
      const orderId = orderStore.selectedOrder?.id;
      if (!orderId) {
        return;
      }
      try {
        const { ticket } = await getOrderCustomFieldAnswers(orderId);
        ticketAnswers.value = ticket;
      } catch (error) {
        LogService.info(
          "Error loading ticket custom fields for download",
          {
            error,
          },
          LogLevel.Error
        );
      }
    });

    // One row per ACTIVE token, joined to its order line for the metadata
    // (category, price). Retail lines have no printable ticket, so skip them.
    // Holder name defaults to the order's customer (from the summary),
    // then override with the ticket's own FIRSTNAME / LASTNAME
    // custom-field answers (informationType lives on the question).
    const ticketsInfo = computed<TicketRow[]>(() => {
      const order = orderStore.selectedOrder;
      if (!order) {
        return [];
      }
      const linesById = new Map(
        orderStore.allOrderLines.map((line): [string, OrderLine] => [
          line.id,
          line,
        ])
      );
      const customer = orderStore.selectedOrderSummary?.customer;
      const defaultFirstName = customer?.firstName ?? "";
      const defaultLastName = customer?.lastName ?? "";

      const rows: TicketRow[] = [];
      for (const token of order.tokens ?? []) {
        if (token.status !== TokenStatus.ACTIVE) {
          continue;
        }
        const line = linesById.get(token.orderLineId);
        if (!line || line.itemType === OrderLinePublicType.RETAIL) {
          continue;
        }

        let firstName = defaultFirstName;
        let lastName = defaultLastName;
        for (const answer of ticketAnswers.value) {
          if (!token.unitItemId || answer.pssUuid !== token.unitItemId) {
            continue;
          }
          const informationType = answer.customField?.informationType;
          if (informationType === InformationType.FIRSTNAME && answer.answer) {
            firstName = answer.answer;
          }
          if (informationType === InformationType.LASTNAME && answer.answer) {
            lastName = answer.answer;
          }
        }

        rows.push({ token, line, firstName, lastName });
      }
      return rows;
    });

    return {
      t,
      modalVisible,
      ticketsInfo,
      currency,
      loading,
      hasInvoiceEnabled,
      OrderLinePublicType,
    };
  },
  methods: {
    closeModal() {
      ModalModule.setStatus(ModalStatus.Hidden);
    },
    saveFile(url: string) {
      const uniqueWindowName = `fileDownload_${Date.now()}`;
      window.open(url, uniqueWindowName);
    },
    async downloadTicket(token: OrderToken) {
      const orderId = useOrderStore().selectedOrder?.id;
      if (!orderId) {
        return;
      }
      this.loading = true;
      try {
        const url = await getDownloadOrderTicket(orderId, token.token);
        if (!url) {
          throw new Error("Error downloading the ticket");
        }
        this.saveFile(url);
      } catch (error) {
        LogService.info(
          `Error downloading ticket for order: ${orderId}`,
          {
            error,
          },
          LogLevel.Error
        );
        NotificationService.showCustomError(this.t("badge.unknown-error"));
      } finally {
        this.loading = false;
      }
    },
    async downloadAllTickets() {
      const orderId = useOrderStore().selectedOrder?.id;
      if (!orderId) {
        return;
      }
      this.loading = true;
      try {
        await postGenerateOrderFiles(orderId);
        const url = await getDownloadOrderTickets(orderId);
        if (!url) {
          throw new Error("Error downloading the tickets");
        }
        this.saveFile(url);
      } catch (error) {
        LogService.info(
          `Error downloading tickets for order: ${orderId}`,
          {
            error,
          },
          LogLevel.Error
        );
        NotificationService.showCustomError(this.t("badge.unknown-error"));
      } finally {
        this.loading = false;
      }
    },
    isMembershipLine(line: OrderLine): boolean {
      return MEMBERSHIP_LINE_TYPES.includes(line.itemType);
    },
    async downloadBadge(token: OrderToken) {
      this.loading = true;
      // Warn (non-blocking) when the order still has an outstanding balance.
      // The old flow checked a per-PSS balance; the order model only exposes an
      // order-level balance (0 = fully paid), so we surface it at that level.
      if (useOrderStore().selectedOrderSummary?.balance) {
        NotificationService.showCustomMessage(
          this.t("badge.not-fully-paid"),
          "warning"
        );
      }

      // For Locarno we have a special Badge layout
      const isLocarno =
        (POSConfigModule.groupId &&
          [19017 /*Locarno*/].includes(POSConfigModule.groupId)) ||
        false;
      try {
        const base64pdf = await getBadgePdf({
          token: token.token,
          isLocarno,
        });
        // download the generated pdf file
        const blob = base64toPdfBlob(base64pdf);
        const url = URL.createObjectURL(blob);
        this.saveFile(url);
        NotificationService.showCustomSuccess(
          this.t("completeorder.download-badge-success")
        );
        this.loading = false;
      } catch (error: any) {
        let errorMessage = "";
        switch (error.message) {
          case "Ticket Id or Uuid must be provided.":
            errorMessage = this.t("badge.invalid-ticket");
            break;
          case "Ticket not found!":
            errorMessage = this.t("badge.ticket-not-found");
            break;
          case "The requested ticket is not a membership":
            errorMessage = this.t("badge.not-membership ");
            break;
          case "Image linked to this membership not found":
            errorMessage = this.t("badge.image-not-found");
            break;
          case "This membership is cancelled.":
            errorMessage = this.t("badge.cancelled");
            break;
          case "Customer picture linked to this membership is not valid or not found":
            errorMessage = this.t("badge.picture-missing");
            break;
          default:
            errorMessage = this.t("badge.unknown-error");
            break;
        }
        NotificationService.showCustomError(errorMessage);
        this.loading = false;
      }
    },
    async downloadGiftcard(token: OrderToken) {
      this.loading = true;
      try {
        const base64pdf = await getGiftcardPdf({
          token: token.token,
        });
        // download the generated pdf file
        const blob = base64toPdfBlob(base64pdf);
        const url = URL.createObjectURL(blob);
        this.saveFile(url);
        NotificationService.showCustomSuccess(
          this.t("completeorder.download-badge-success")
        );
        this.loading = false;
      } catch (error: any) {
        let errorMessage = "";
        switch (error.message) {
          case "Ticket Id or Uuid must be provided.":
            errorMessage = this.t("badge.invalid-ticket");
            break;
          case "Ticket not found!":
            errorMessage = this.t("badge.ticket-not-found");
            break;
          case "This giftcard is cancelled.":
            errorMessage = this.t("badge.cancelled");
            break;
          default:
            errorMessage = this.t("badge.unknown-error");
            break;
        }
        NotificationService.showCustomError(errorMessage);
        this.loading = false;
      }
    },
    async downloadInvoice() {
      const orderId = useOrderStore().selectedOrder?.id;
      if (!orderId) {
        return;
      }
      this.loading = true;
      try {
        // No invoiceId is exposed on the order, so always (re)generate before
        // downloading — the backend generate is keyed by order id.
        await generateOrderInvoice(orderId);
        const url = await downloadOrderInvoice(orderId);
        if (!url) {
          throw new Error("Error downloading the invoice");
        }
        this.saveFile(url);
      } catch (error) {
        LogService.info(
          `Error downloading invoice for order: ${orderId}`,
          {
            error,
          },
          LogLevel.Error
        );
        NotificationService.showGeneralResponseError();
      } finally {
        this.loading = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@use "@/styles/variables.scss" as *;

.download-tickets-popup {
  position: relative;
}
.download-button {
  width: 40px;
  border-color: white;
  font-size: 30px;
  color: black;
  padding: 0;
}
.column {
  word-break: keep-all;
}

.spinner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;

  .spinner {
    font-size: 48px;
    color: $color-dark-cyan;
  }
}
</style>
